import { createContext, useContext } from 'react'

import type { AuthConfig, AuthContextValue } from './types.ts'

// Deliberately untyped. One provider serves apps whose caller and config shapes
// differ, and a React context cannot carry a type parameter — so each app states
// its two types exactly once, where it re-exports `useAuth`.
export const AuthContext = createContext<unknown>(null)

/**
 * The session for the app that mounted the provider. Both type arguments are the
 * consuming app's: pin them once in a re-export — `export const useAuth = () =>
 * useSharedAuth<Caller, PortalConfig>()` — so screens call it bare.
 */
export const useAuth = <
  TCaller,
  TConfig extends AuthConfig = AuthConfig,
>(): AuthContextValue<TCaller, TConfig> => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context as AuthContextValue<TCaller, TConfig>
}
